import { css } from '@emotion/react';
import {
  type FC, type ReactNode, useMemo,
} from 'react';
import { LabelInCircleComponent } from '~/_shared/baseComponents/labelInShape/labelInCircleComponent';
import { TinyMapColorTileComponent } from '~/_shared/baseComponents/tinyMapPlaceholder';
import { type ColorResult } from '~/_shared/components/colorPicker/colorPicker.types';
import { useTheme } from '~/_shared/themes/theme.hooks';
import { changeColorAlpha } from '~/_shared/utils/colors/colors.helpers';
import { FillOpacitySliderComponent } from '~/boundary/settings/fillOpacitySlider/fillOpacitySlider.component';
import { BoundarySettingsTableCellWrapperComponent } from '~/boundary/settings/table/row/BoundarySettingsTableCellWrapper.component';
import { BoundarySettingsTableRowWrapperComponent } from '~/boundary/settings/table/row/BoundarySettingsTableRowWrapper.component';
import { ColorPickerComponent } from '../../colorPicker/colorPicker.component';

const SLIDER_WIDTH = 60; // note that if you want to set this to a lower value, double check if it's still well usable across devices
const SLIDER_INPUT_WIDTH = 60;
const OPACITY_CONTROL_GAP = 12;
const tableFillColorWrapperStyle = css({
  display: 'flex',
  alignItems: 'center',
});

const opacityStyle = css({
  boxSizing: 'border-box',
  width: SLIDER_WIDTH + OPACITY_CONTROL_GAP + SLIDER_INPUT_WIDTH,
});

const groupNameStyle = css({
  overflow: 'visible',
  textOverflow: 'clip',
  whiteSpace: 'normal',
});

const mapTileStyle = css({
  marginLeft: 8,
});

type BoundarySettingsTableRowProps = Readonly<{
  index: number;
  data: ReactNode;
  color: ColorResult;
  lineColor: ColorResult;
  lineWidth: number;
  hideOpacity?: boolean;
  onColorChange: (color: ColorResult) => void;
}>;

export const BoundarySettingsTableRowComponent: FC<BoundarySettingsTableRowProps> = (props) => {
  const opacity = (props.color.rgba.a ?? 1) * 100;

  const changeColorOpacity = (alpha: number) => {
    const newColor = changeColorAlpha(props.color.rgba, alpha / 100);
    props.onColorChange(newColor);
  };

  // the picker has no alpha control, so the row's opacity is carried over
  const changeColor = (color: ColorResult) => {
    props.onColorChange(changeColorAlpha(color.rgba, props.color.rgba.a));
  };

  return (
    <BoundarySettingsTableRowWrapperComponent>
      <BoundarySettingsTableCellWrapperComponent>
        <div css={tableFillColorWrapperStyle}>
          <RowNumber number={props.index} />
          <ColorPickerComponent
            onChange={changeColor}
            selectedColor={props.color}
            displayAlpha={false}
            size={22}
          />
          <TinyMapColorTileComponent
            color={props.color.rgba}
            lineColor={props.lineColor.rgba}
            lineWidth={Math.ceil(props.lineWidth / 2)}
            css={mapTileStyle}
            size="Large"
          />
        </div>
      </BoundarySettingsTableCellWrapperComponent>

      {!props.hideOpacity && (
        <BoundarySettingsTableCellWrapperComponent>
          <FillOpacitySliderComponent
            css={opacityStyle}
            onChange={changeColorOpacity}
            value={opacity}
          />
        </BoundarySettingsTableCellWrapperComponent>
      )}

      <BoundarySettingsTableCellWrapperComponent css={groupNameStyle}>
        {props.data}
      </BoundarySettingsTableCellWrapperComponent>
    </BoundarySettingsTableRowWrapperComponent>
  );
};

const tableCounterStyle = css({
  marginRight: 12,
});

const RowNumber: FC<{ number: number }> = ({ number }) => {
  const theme = useTheme();
  const circleColors = useMemo(() => ({
    fillColor: theme.backgroundColors.secondaryHover,
    fontColor: theme.textColors.primary,
  }), [theme.backgroundColors.secondaryHover, theme.textColors.primary]);

  return (
    <LabelInCircleComponent
      colors={circleColors}
      css={tableCounterStyle}
      size={24}
    >
      {number}
    </LabelInCircleComponent>
  );
};
